import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import ErrorState from './ErrorState';
import { track } from '../analytics';

interface ErrorBoundaryState {
    hasError: boolean;
}

/**
 * The last line of defence around the router.
 *
 * Without one, a throw during render unmounts the whole tree and the resident
 * gets a blank page with no explanation and no way back (F30). React offers no
 * hook equivalent, so this stays a class.
 *
 * It deliberately does not try to recover in place: the state that produced the
 * throw is still there, and re-rendering it would throw again. A reload is the
 * honest offer.
 */
class ErrorBoundary extends React.Component<WithTranslation & { children: React.ReactNode }, ErrorBoundaryState> {
    state: ErrorBoundaryState = { hasError: false };

    static getDerivedStateFromError(): ErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        // The error's *name*, never its message: a message can quote a value
        // the resident typed, and this leaves the device.
        track('error_render', { error: error.name || 'Unknown' });

        // The console keeps the detail. The component stack is the only clue a
        // bug report will carry, and it must not be sent anywhere.
        console.error('Unhandled render error:', error, info.componentStack);
    }

    handleReload = () => {
        window.location.reload();
    };

    render() {
        const { t, children } = this.props;

        if (this.state.hasError) {
            return (
                <ErrorState
                    message={t('unexpected_error')}
                    onRetry={this.handleReload}
                    retryLabel={t('reload_app')}
                />
            );
        }

        return children;
    }
}

export default withTranslation()(ErrorBoundary);
